import {Component, Input} from '@angular/core';
import {EventResponse, UserResponse} from '../../../../@shared/models/response.module';
import {EventsService} from '../../../../@shared/services/events-service';
import {AQH_EVE_0} from '../../../../@shared/models/input.module';
import {TranslateService} from '@ngx-translate/core';

type CategoryFilter = 'all' | 'competition' | 'training' | 'social';

@Component({
  selector: 'app-user-profile-shows',
  standalone: false,
  templateUrl: './user-profile-shows.html',
  styleUrl: './user-profile-shows.css'
})
export class UserProfileShows {
  @Input() user: UserResponse;

  allEvents: EventResponse[] = [];
  loading: boolean = true;
  categoryFilter: CategoryFilter = 'all';

  constructor(private eventsService: EventsService,
              private translateService: TranslateService) {
  }

  ngOnInit() {
    if (this.user?.user_id) {
      this.loadUserEvents();
    }
  }

  ngOnChanges() {
    if (this.user?.user_id) {
      this.loadUserEvents();
    }
  }

  loadUserEvents() {
    this.loading = true;
    const params: AQH_EVE_0 = {
      registered_user_id: this.user.user_id,
      paginate: 0
    };

    this.eventsService.getEvents(params).subscribe({
      next: (res) => {
        this.allEvents = res.data || [];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  get filteredEvents(): EventResponse[] {
    if (this.categoryFilter === 'all') {
      return this.allEvents;
    }
    return this.allEvents.filter(e => this.getEventCategory(e) === this.categoryFilter);
  }

  get upcomingEvents(): EventResponse[] {
    const now = new Date();
    return this.filteredEvents
      .filter(e => e.start_date && new Date(e.start_date) >= now)
      .sort((a, b) => {
        const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
        const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
        return dateA - dateB; // Ascending order for upcoming
      });
  }

  get pastEvents(): EventResponse[] {
    const now = new Date();
    return this.filteredEvents
      .filter(e => e.start_date && new Date(e.start_date) < now)
      .sort((a, b) => {
        const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
        const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
        return dateB - dateA; // Descending order for past (most recent first)
      });
  }

  get competitionCount(): number {
    return this.allEvents.filter(e => this.getEventCategory(e) === 'competition').length;
  }

  get trainingCount(): number {
    return this.allEvents.filter(e => this.getEventCategory(e) === 'training').length;
  }

  get socialCount(): number {
    return this.allEvents.filter(e => this.getEventCategory(e) === 'social').length;
  }

  getEventCategory(event: EventResponse): string {
    if (event.category) {
      return event.category;
    }
    // Fallback to event_type_id
    if (event.event_type_id === 0) return 'competition';
    if (event.event_type_id === 2) return 'training';
    if (event.event_type_id === 1) return 'social';
    return 'competition';
  }

  getCategoryLabel(event: EventResponse): string {
    const category = this.getEventCategory(event);
    switch (category) {
      case 'competition': return this.translateService.instant('userEvents.categoryCompetition');
      case 'training': return this.translateService.instant('userEvents.categoryTraining');
      case 'social': return this.translateService.instant('userEvents.categorySocial');
      default: return this.translateService.instant('userEvents.categoryEvent');
    }
  }

  getCategoryBadgeClass(event: EventResponse): string {
    const category = this.getEventCategory(event);
    switch (category) {
      case 'competition': return 'badge-competition';
      case 'training': return 'badge-training';
      case 'social': return 'badge-social';
      default: return 'badge-light-primary';
    }
  }

  isUpcoming(event: EventResponse): boolean {
    if (!event.start_date) return false;
    return new Date(event.start_date) >= new Date();
  }

  formatDateDMYDots(dateValue: string | Date | null | undefined): string {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  }

  getGoogleMapsUrl(event: EventResponse): string {
    const address = encodeURIComponent(`${event.address || ''}, ${event.postal_code || ''} ${event.city || ''}, ${event.country || ''}`);
    return `https://www.google.com/maps/search/?api=1&query=${address}`;
  }
}
